// 顶部固定栏：全局搜索 + 通知 + 管理员菜单（对齐原演示 banner 右侧）
import { Bell, Search, ChevronDown, User, LogOut, Settings } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/components/Toast';
import { logout } from '@/api/auth';
import { loadSession } from '@/api/client';

// 搜索关键字 → 路由映射（演示级快捷跳转，对齐 /platform/* 路由）
const SEARCH_MAP: { keywords: string[]; path: string; label: string }[] = [
  { keywords: ['卡片总览', '卡片dashboard', 'card dashboard'], path: '/platform/card/dashboard', label: '全局卡片总览' },
  { keywords: ['卡号检索', '卡号搜索', 'card search'], path: '/platform/card/search', label: '卡号精准检索' },
  { keywords: ['卡片操作', '冻结', '解冻', 'card ops'], path: '/platform/card/ops', label: '卡片操作' },
  { keywords: ['卡片', '虚拟卡', 'card'], path: '/platform/card/ops', label: '卡片操作' },
  { keywords: ['商户卡片', 'by merchant'], path: '/platform/card/by-merchant', label: '商户卡片筛选' },
  { keywords: ['交易流水', 'card transactions'], path: '/platform/card/transactions', label: '卡片交易流水' },
  { keywords: ['实体卡', 'physical'], path: '/platform/card/physical/inventory', label: '实体卡库存' },
  { keywords: ['制卡批次', 'batch'], path: '/platform/card/physical/batches', label: '制卡批次追踪' },
  { keywords: ['实时交易', '实时', 'realtime', 'stream'], path: '/platform/card/tx/stream', label: '实时交易流' },
  { keywords: ['消费明细', 'tx query'], path: '/platform/card/tx/query', label: '全局消费明细' },
  { keywords: ['商户', 'merchant'], path: '/platform/account/merchants', label: '商户管理' },
  { keywords: ['代理商', 'agent'], path: '/platform/account/agents', label: '代理商管理' },
  { keywords: ['价差监控', 'spread monitor'], path: '/platform/account/agents/AGT001/spread-monitor', label: '代理商价差监控' },
  { keywords: ['等级', '授权价', 'tier price'], path: '/platform/account/agents/AGT001/tier-price', label: '代理商等级与授权价' },
  { keywords: ['审核', '开户', 'onboard', 'audit', '代理商资质', '代理kyc', '代理KYC', 'kyb'], path: '/platform/account/audit/agent-onboarding', label: '代理商资质审核' },
  { keywords: ['商户开户', 'merchant onboarding', '商户资质', '商户kyb', '商户KYB'], path: '/platform/account/audit/merchant-onboarding', label: '商户开户审核' },
  { keywords: ['资质变更', 'kyc change', '商户变更', 'profile change', '字段diff'], path: '/platform/account/audit/merchant-kyc-change', label: '资质变更审核' },
  { keywords: ['批量审核', 'batch audit'], path: '/platform/account/audit/batch', label: '批量审核' },
  { keywords: ['入金', '充值', 'deposit'], path: '/platform/finance/deposit-reviews', label: '入金审核' },
  { keywords: ['提现', '佣金', 'withdraw'], path: '/platform/finance/withdrawal-reviews', label: '提现审核' },
  { keywords: ['对账单', 'statement'], path: '/platform/finance/merchant-statements', label: '商户对账单' },
  { keywords: ['价差结算', 'spread settlement'], path: '/platform/finance/agent-spread-settlements', label: '代理商价差结算' },
  { keywords: ['强制退款', 'force refund'], path: '/platform/finance/force-refunds', label: '强制退款工作台' },
  { keywords: ['资金', '钱包', 'finance'], path: '/platform/finance/overview', label: '资金概览' },
  { keywords: ['调拨', 'transfer'], path: '/platform/finance/fund-transfers', label: '资金调拨' },
  { keywords: ['资金池', '币种', 'treasury'], path: '/platform/finance/treasury-positions', label: '多币种资金池' },
  { keywords: ['对账', 'reconciliation'], path: '/platform/finance/reconciliation', label: '对账管理' },
  { keywords: ['结算', 'settlement'], path: '/platform/finance/settlement-batches', label: '结算批次管理' },
  { keywords: ['托管', 'escrow', '备付金'], path: '/platform/finance/escrow', label: '托管管理' },
  { keywords: ['风险', '风控', 'risk'], path: '/platform/risk/alerts', label: '风险预警' },
  { keywords: ['黑名单', '白名单', 'blocklist'], path: '/platform/risk/blocklist', label: '黑白名单管理' },
  { keywords: ['kyc复审', '活体审核', '身份认证', '持卡人kyc', '持卡人KYC'], path: '/platform/risk/kyc-review', label: '持卡人 KYC 复审' },
  { keywords: ['罚款', '欠费', '巡检罚款', 'fine'], path: '/platform/risk/kyc-inspect-fines', label: 'KYC 罚款台账' },
  { keywords: ['争议', '拒付', 'dispute'], path: '/platform/risk/disputes', label: '争议处理' },
  { keywords: ['合规', '监管', '法律', 'compliance'], path: '/platform/risk/compliance', label: '合规管理' },
  { keywords: ['aml', '洗钱'], path: '/platform/risk/aml', label: 'AML 合规检查' },
  { keywords: ['上游渠道', '通道', 'channel', 'BIN', '通道管理', '通道映射'], path: '/platform/account/upstream-channels', label: '上游渠道' },
  { keywords: ['产品', '费率', 'product'], path: '/platform/account/products', label: '产品管理' },
  { keywords: ['工单', '客服', 'ticket'], path: '/platform/ops/cs/tickets', label: '客服工单' },
  { keywords: ['邮件拦截', 'inbound email'], path: '/platform/ops/email/inbound', label: '上游邮件拦截' },
  { keywords: ['驾驶舱', 'cockpit'], path: '/platform/ops/cockpit', label: '运营驾驶舱' },
  { keywords: ['配置中心', 'config'], path: '/platform/ops/config', label: '配置中心' },
  { keywords: ['日志分析', '日志异常', 'log analysis'], path: '/platform/ops/log-analysis', label: '日志异常分析' },
  { keywords: ['监控', '告警', 'alert', 'sre'], path: '/platform/ops/sre/alerts', label: '监控大盘与告警' },
  { keywords: ['报表', 'report'], path: '/platform/report/dashboard', label: 'BI 数据看板' },
  { keywords: ['报表导出', 'export'], path: '/platform/report/export', label: '报表导出' },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const navigate = useNavigate();
  const toast = useToast();
  // 用户身份：优先 me.principal，回退 session.principal，最终兜底演示账号
  const session = loadSession();
  const principal = session?.me?.principal || session?.principal || 'admin@allinks.io';

  // 下拉打开时用 document 监听关菜单，禁止 fixed inset-0 遮罩吞掉页面 CTA
  useEffect(() => {
    if (!open && !notifOpen) return;
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as HTMLElement | null;
      if (target?.closest('[data-header-popover]')) return;
      setOpen(false);
      setNotifOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open, notifOpen]);

  const handleSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter' || !keyword.trim()) return;
    const kw = keyword.trim().toLowerCase();
    const match = SEARCH_MAP.find((s) => s.keywords.some((k) => kw.includes(k.toLowerCase()) || k.toLowerCase().includes(kw)));
    if (match) {
      navigate(match.path);
      toast.info(`已跳转到「${match.label}」`);
      setKeyword('');
    } else {
      toast.error('未找到匹配的页面，请尝试：卡片 / 商户 / 入金 / 风险 等');
    }
  };

  return (
    <header className="h-14 flex-shrink-0 bg-white border-b border-slate-200 flex items-center justify-between px-6">
      {/* 左侧留空（页面标题由 PageContainer 在 main 顶部渲染） */}
      <div className="flex-1" />

      {/* 右侧：搜索 + 通知 + 管理员 */}
      <div className="flex items-center gap-3">
        <div className="relative group">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 transition-colors group-focus-within:text-brand-500" />
          <input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onKeyDown={handleSearch}
            className="w-64 pl-9 pr-3 py-1.5 rounded-lg bg-slate-50 border border-slate-200 text-sm placeholder:text-slate-400 focus:outline-none focus:bg-white focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 focus:w-80 transition-all duration-200"
            placeholder="搜索页面（回车跳转）..."
          />
        </div>

        <div className="relative" data-header-popover>
          <button
            type="button"
            onClick={() => { setNotifOpen((v) => !v); setOpen(false); }}
            className="relative p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer"
            title="消息通知"
            aria-label="消息通知"
          >
            <Bell className="w-4.5 h-4.5" />
            <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full border border-white" />
          </button>

          {notifOpen && (
            <>
              <div className="absolute right-0 top-full mt-1 w-72 card py-1 z-20 animate-slide-up shadow-pop">
                <div className="px-3 py-2 border-b border-slate-100">
                  <p className="text-sm font-medium text-slate-700">消息通知</p>
                </div>
                {[
                  { title: '入金待审核', desc: '3 笔入金申请待处理', dot: 'bg-amber-500' },
                  { title: '商户开户待审核', desc: '2 家商户入驻申请待审核', dot: 'bg-blue-500' },
                  { title: '风险预警', desc: '发现 1 条高危告警', dot: 'bg-red-500' },
                ].map((n) => (
                  <button
                    type="button"
                    key={n.title}
                    onClick={() => {
                      setNotifOpen(false);
                      navigate('/platform/notifications', {
                        state: { notice: { title: n.title, body: n.desc } },
                      });
                    }}
                    className="w-full flex items-start gap-2.5 px-3 py-2.5 text-left hover:bg-slate-50 transition-colors cursor-pointer"
                  >
                    <span className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${n.dot}`} />
                    <span>
                      <span className="block text-sm text-slate-700">{n.title}</span>
                      <span className="block text-xs text-slate-400 mt-0.5">{n.desc}</span>
                    </span>
                  </button>
                ))}
                <div className="px-3 py-2 border-t border-slate-100">
                  <button
                    type="button"
                    onClick={() => { setNotifOpen(false); navigate('/platform/notifications'); }}
                    className="w-full text-center text-xs font-medium text-brand-600 hover:text-brand-700 cursor-pointer"
                  >
                    查看全部通知
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="relative" data-header-popover>
          <button
            type="button"
            onClick={() => { setOpen((v) => !v); setNotifOpen(false); }}
            className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
          >
            <div className="w-7 h-7 rounded-full bg-brand-600 text-white flex items-center justify-center text-xs font-semibold">
              A
            </div>
            <div className="text-left">
              <p className="text-sm font-medium text-slate-800 leading-tight">{principal}</p>
              <p className="text-[11px] text-slate-400 leading-tight">{principal}</p>
            </div>
            <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
          </button>

          {open && (
            <>
              <div className="absolute right-0 top-full mt-1 w-48 card py-1 z-20">
                <div className="px-3 py-2 border-b border-slate-100">
                  <p className="text-sm font-medium text-slate-700">{principal}</p>
                  <p className="text-xs text-slate-400">{principal}</p>
                </div>
                <button
                  type="button"
                  onClick={() => { setOpen(false); navigate('/platform/system/settings'); toast.info('已打开基础设置'); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50"
                >
                  <User className="w-3.5 h-3.5" />个人资料
                </button>
                <button
                  type="button"
                  onClick={() => { setOpen(false); navigate('/platform/system/security'); toast.info('已打开安全配置'); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50"
                >
                  <Settings className="w-3.5 h-3.5" />安全设置
                </button>
                <button
                  type="button"
                  onClick={() => { setOpen(false); void logout(); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                >
                  <LogOut className="w-3.5 h-3.5" />退出登录
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
